import { posthog } from 'posthog-js';
import type { PostHog } from 'posthog-js';
import { Properties, TrackOptions } from './StackTrackerService';
import { PageViewData } from './BaseTrackingService';
import { FEATURE_FLAG } from '~/app/enums/FeatureFlag';
import { useAuthStore } from '~/stores/authStore';
import { useCobrowseStore } from '~/stores/cobrowseStore';

export class PostHogTrackerService implements TrackerService {
    protected posthogClient: PostHog;
    constructor() {
        const {
            public: { posthogApiToken, posthogApiUrl, posthogUiHost, posthogDebug },
            app: { buildId },
        } = useRuntimeConfig();

        const featureFlags: Record<string, boolean> = {};
        Object.values(FEATURE_FLAG).forEach((featureFlagName) => (featureFlags[featureFlagName] = false));

        const { query } = useRoute();
        const distinctID = typeof query.ph_distinct_id === 'string' ? query.ph_distinct_id : undefined;
        const sessionID = typeof query.ph_session_id === 'string' ? query.ph_session_id : undefined;

        posthog.init(posthogApiToken, {
            api_host: posthogApiUrl,
            ui_host: posthogUiHost,
            disable_session_recording: false,
            session_recording: {
                maskAllInputs: false,
                maskInputOptions: {
                    password: true,
                },
            },
            autocapture: false,
            debug: posthogDebug,
            bootstrap: {
                featureFlags,
                ...(distinctID && { distinctID }),
                ...(sessionID && { sessionID }),
            },
            // Tags every event with the build that produced the running JS. Done here
            // rather than via register() because super properties are wiped by reset(),
            // which fires on logout and impersonation switches.
            before_send: (event) => {
                // Staff impersonation sends nothing, including what PostHog captures on its own
                // ($identify, $pageleave, $exception autocapture, recordings) on behalf of the client.
                if (!event || this.isImpersonating) return null;

                const { sessionId: cobrowseSessionId } = useCobrowseStore();
                // Same reason for the tenant group and the unpaid level (TDD-6833): every event lands on
                // the company the mobile app already groups by, and a return to level 0 shows on any event.
                const { tenant } = useAuthStore();
                event.properties = {
                    ...(tenant && { unpaid_level: tenant.unpaidLevel }),
                    ...event.properties,
                    ...(tenant && { $groups: { ...event.properties?.$groups, tenant: tenant.getId() } }),
                    app_build_id: buildId,
                    ...(cobrowseSessionId && { cobrowse_session_id: cobrowseSessionId }),
                };

                return event;
            },
        });

        this.posthogClient = posthog;
    }

    track(event: string, properties?: Properties, options?: TrackOptions): void {
        if (this.isImpersonating) return;
        // posthog-js flushes its queue on `pagehide` before later listeners run: a queued event would stay unsent.
        this.posthogClient.capture(event, properties, options?.beacon ? { send_instantly: true, transport: 'sendBeacon' } : undefined);
    }

    identify(userId: string, properties?: Properties): void {
        // Reset first so the impersonated user's identify() aliases from a fresh anonymous id
        // instead of merging with whatever identity (e.g. the admin's) the browser held before.
        if (this.isImpersonating) {
            this.posthogClient.reset();
        }
        this.posthogClient.identify(userId, properties);
    }

    setPersonProperties(properties: Properties): void {
        if (this.isImpersonating) return;
        this.posthogClient.setPersonProperties(properties);
    }

    group(type: string, key: string, properties?: Properties): void {
        if (this.isImpersonating) return;
        this.posthogClient.group(type, key, properties);
    }

    pageView(properties?: PageViewData): void {
        this.track('$pageview', properties);
    }

    captureException(error: unknown, properties?: Properties): void {
        if (this.isImpersonating) return;
        this.posthogClient.captureException(error, properties);
    }

    reset(): void {
        this.posthogClient.reset();
    }

    private get isImpersonating(): boolean {
        return useAuthStore().isImpersonating;
    }
}
